import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { getUserStatus } from '@helpers/getUserStatus';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useChartsReadingTypesQuery } from '@services/useChartsReadingTypesQuery';
import { useLanguageListQuery } from '@services/useLanguageListQuery';
import { useTimezoneQuery } from '@services/useTimezoneQuery';
import { useUserCreateMutation } from '@services/user/useUserCreateMutation';
import { useUserEmailEventDictionaryQuery } from '@services/user/useUserEmailEventDictionaryQuery';
import { useUserQuery } from '@services/user/useUserQuery';
import { useUserUpdateMutation } from '@services/user/useUserUpdateMutation';
import { useUserValidateDataMutation } from '@services/user/useUserValidateDataMutation';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import UserModalSkeleton from './UserModalSkeleton';
import Avatar from './components/Avatar/Avatar';
import DeleteUserBtn from './components/DeleteUserBtn/DeleteUserBtn';
import ResendEmailBtn from './components/ResendEmailBtn/ResendEmailBtn';
import StepsInfo from './components/StepsInfo/StepsInfo';
import { USER_FIELDS, userDefaultValues } from './constants/schemaUser';
import { getUserFormSteps } from './helpers/getUserFormSteps';
import { selectSubscriptions, useSubscriptionsUserActions } from './store/useSubscriptionsUserStore';
import style from './style.module.scss';

const UserModal = ({ userId }) => {
  const { t } = useTranslation();

  const rtl = selectAppRtl();
  const [step, setStep] = useState(0);
  const [metricsValue, setMetricsValue] = useState(1);

  const { data: emailEventsData } = useUserEmailEventDictionaryQuery();
  const { data: userData, isFetching } = useUserQuery({ userId });
  const { data: chartsReadingTypesData } = useChartsReadingTypesQuery();
  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: timezoneListData } = useTimezoneQuery();
  const { data: languageListData } = useLanguageListQuery();

  const { mutate: userValidateDataMutate } = useUserValidateDataMutation();
  const { mutate: userCreateMutate, isLoading: createMutationLoading } = useUserCreateMutation();
  const { mutate: userUpdateMutate, isLoading: updateMutationLoading } = useUserUpdateMutation({ userId });

  const { setModalClose, setSecondaryModal } = useModalActions();
  const { setDefaultSubscriptions } = useSubscriptionsUserActions();

  const user = userData?.user;
  const userStatus = getUserStatus(user);

  const stepsList = getUserFormSteps(metricsValue);
  const currentStep = stepsList[step];
  const defaultFormValues = userDefaultValues(
    t,
    userData,
    emailEventsData?.simpleEventsDictionary,
    assetTreeData,
    languageListData,
    timezoneListData,
  );
  const CurrentStepComponent = currentStep?.stepComponent;

  const subscriptionsValue = selectSubscriptions();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    setError,
    clearErrors,
    formState: { isDirty },
  } = useForm({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
    resolver: currentStep?.schema,
  });

  const setNotification = () => {
    const userSubscriptions = Object.values(user?.email_subscriptions || {}).filter(
      (ev) => ev.event?.with_settings,
    );

    setDefaultSubscriptions({
      emailEventsDictionary: emailEventsData?.emailEvents,
      userSubscriptions: !!userSubscriptions.length && userSubscriptions,
      defaultLocs: assetTreeData?.facilities?.[0]?.id ? [assetTreeData?.facilities?.[0]?.id] : [],
    });
  };

  useEffect(() => {
    setNotification();
  }, [user, emailEventsData]);

  useEffect(() => {
    reset(defaultFormValues);
  }, [userData, emailEventsData]);

  const [emailValue, usernameValue, simple_email_subscriptions, metric] = watch([
    USER_FIELDS.EMAIL,
    USER_FIELDS.USERNAME,
    USER_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS,
    USER_FIELDS.USE_METRIC,
  ]);

  useEffect(() => {
    setMetricsValue(metric);
  }, [metric]);

  const onSuccess = () => {
    setModalClose();
    showToast({ variant: 'success', message: t('Your changes have been saved.') });

    setNotification();
  };

  const onError = (error) => {
    showToast({ variant: 'error', message: error.response.data.message });
  };

  const submit = handleSubmit((formData) => {
    const aiUrgencies = [];

    for (let key in formData.action_list_urgencies) {
      if (formData.action_list_urgencies[key]) {
        aiUrgencies.push(key);
      }
    }

    const units = {};

    if (chartsReadingTypesData?.length) {
      chartsReadingTypesData.forEach(
        (readingType) => (units[readingType.id] = +formData.use_metric === 0 ? 'metric' : 'us'),
      );
    }

    let data = {
      ...formData,
      action_list_urgencies: aiUrgencies,
      removeImage: false,
      language_iso_code: formData.language_iso_code.iso_code,
      locations: formData.all_facilities ? [] : formData.locations.map((val) => ({ value: +val })),
      email_events: JSON.stringify(subscriptionsValue),
      permissions: {
        'ww2-dashboard': formData.viewSensorDataPermission ? ['viewSensorData'] : [],
      },
      personal_settings: {
        units,
        datetime_format: formData.datetime_format,
      },
      simple_email_subscriptions,
      timezone_id: formData.timezone_id.id,
      viewSensorDataPermission: +formData.viewSensorDataPermission,
      receive_own_emails: +formData.receive_own_emails,
    };

    if (userId) {
      data.id = userId;
      if (!+data.is_active) {
        data.confirmed = 0;
      } else if (+user.is_active !== +data.is_active) {
        data.confirmed = 0;
        data.set_active = true;
      }

      userUpdateMutate(data, { onSuccess, onError });
    } else {
      delete data.is_active;
      userCreateMutate(data, { onSuccess, onError });
    }
  });

  const handleClose = () => {
    if (isDirty) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Are You Sure You Want To End The Action Without Saving The Data'),
          onConfirm: () => {
            setModalClose();
            reset();
            setStep(0);
          },
        },
      });
    } else {
      reset();
      setModalClose();
    }
  };

  const handleBack = () => {
    const previousStep = currentStep.previousStepIndex;
    if (previousStep === 'close') {
      handleClose();

      return;
    }

    setStep(previousStep);
  };

  const handleNextStep = async () => {
    const nextStepIndex = currentStep.nextStepIndex;

    const isValid = await trigger();

    if (isValid && nextStepIndex === 'end') {
      submit();

      return;
    }

    if (isValid) {
      if (currentStep?.step === 1 && !user?.id) {
        userValidateDataMutate(
          { email: emailValue, username: usernameValue },
          {
            onSuccess: () => {
              clearErrors();
              setStep(nextStepIndex);
            },
            onError: (err) => {
              if (err.response.data.errors?.email) {
                setError(USER_FIELDS.EMAIL, { message: err.response.data.errors?.email[0] });
              }
              if (err.response.data.errors?.username) {
                setError(USER_FIELDS.USERNAME, { message: err.response.data.errors?.username[0] });
              }
            },
          },
        );

        return;
      }

      setStep(nextStepIndex);
    }
  };

  return (
    <div
      className={cx(style.userModal, {
        [style.bodyRTL]: rtl,
        [style.customUnits]: metric === 2 && step === 2,
      })}
    >
      <ModalHeader
        title={t(userId ? 'Edit User' : 'Add User')}
        variant={'withoutSeparator'}
        onClose={handleClose}
      />
      <StepsInfo userFormSteps={stepsList} userId={userId} step={step} />

      <div className={style.modalBody}>
        {isFetching && <UserModalSkeleton />}
        {!isFetching && (
          <>
            {currentStep.step !== 4 && (
              <div className={style.modalBodyUser}>
                <Avatar
                  control={control}
                  userId={userId}
                  userStatus={userStatus}
                  showChangePhotoBtn={step === 0}
                />
              </div>
            )}
            <CurrentStepComponent
              control={control}
              user={user}
              languageList={languageListData}
              timezoneList={timezoneListData}
            />
          </>
        )}
      </div>
      <div className={style.modalFooter}>
        <div>
          {user?.id && (
            <>
              {userStatus === 'Unconfirmed' && <ResendEmailBtn user={user} />}
              <DeleteUserBtn user={user} />
            </>
          )}
        </div>
        <div className={style.btnBlock}>
          <Button
            type="secondary"
            text={t(currentStep?.step === 1 ? 'Cancel' : 'Back')}
            onClick={handleBack}
          />
          <Button
            type="primary"
            variant={'medium'}
            text={t(currentStep?.nextStepIndex === 'end' ? 'Submit' : 'Next')}
            disabled={createMutationLoading || updateMutationLoading}
            onClick={handleNextStep}
          />
        </div>
      </div>
    </div>
  );
};

export default UserModal;
